import { useMemo, useCallback, createElement, useState } from 'react';
import Highlight, { defaultProps } from 'prism-react-renderer';
import styled, { css } from 'styled-components';
import DefaultPrismTheme from 'prism-react-renderer/themes/nightOwl';
function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}
function _taggedTemplateLiteralLoose(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  strings.raw = raw;
  return strings;
}
function _templateObject5() {
  var data = _taggedTemplateLiteralLoose(["\n  display: inline-flex;\n  justify-content: flex-end;\n  margin-right: 0.75rem;\n  width: 2em;\n  user-select: none;\n  opacity: 0.6;\n"]);
  _templateObject5 = function _templateObject5() {
    return data;
  };
  return data;
}
function _templateObject4() {
  var data = _taggedTemplateLiteralLoose(["\n          padding: 0 1rem;\n        "]);
  _templateObject4 = function _templateObject4() {
    return data;
  };
  return data;
}
function _templateObject3() {
  var data = _taggedTemplateLiteralLoose(["\n          padding: 0 1rem 0 0;\n        "]);
  _templateObject3 = function _templateObject3() {
    return data;
  };
  return data;
}
function _templateObject2() {
  var data = _taggedTemplateLiteralLoose(["\n  ", "\n"]);
  _templateObject2 = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject() {
  var data = _taggedTemplateLiteralLoose(["\n  border-radius: 3px;\n  font-size: 1rem;\n  line-height: 1.4;\n  text-align: start;\n  padding: 1rem 0;\n  overflow: auto;\n"]);
  _templateObject = function _templateObject() {
    return data;
  };
  return data;
}
var Pre = /*#__PURE__*/styled.pre( /*#__PURE__*/_templateObject());
var Line = /*#__PURE__*/styled.div( /*#__PURE__*/_templateObject2(), function (_ref) {
  var showLineNumbers = _ref.showLineNumbers;
  return showLineNumbers ? css(_templateObject3()) : css(_templateObject4());
});
var LineNumber = /*#__PURE__*/styled.span( /*#__PURE__*/_templateObject5());
var Code = function Code(_ref2) {
  var _ref2$children = _ref2.children,
    children = _ref2$children === void 0 ? function (_ref3) {
      var Preview = _ref3.Preview;
      return createElement(Preview, null);
    } : _ref2$children,
    classNameOverride = _ref2.className,
    code = _ref2.code,
    _ref2$language = _ref2.language,
    language = _ref2$language === void 0 ? 'javascript' : _ref2$language,
    _ref2$theme = _ref2.theme,
    theme = _ref2$theme === void 0 ? DefaultPrismTheme : _ref2$theme,
    showLineNumbers = _ref2.showLineNumbers,
    _ref2$lineNumberStart = _ref2.lineNumberStart,
    lineNumberStart = _ref2$lineNumberStart === void 0 ? 1 : _ref2$lineNumberStart,
    _ref2$focusedLines = _ref2.focusedLines,
    focusedLines = _ref2$focusedLines === void 0 ? '' : _ref2$focusedLines,
    restProps = _objectWithoutPropertiesLoose(_ref2, ["children", "className", "code", "language", "theme", "showLineNumbers", "lineNumberStart", "focusedLines"]);
  var focusedLineNumbers = useMemo(function () {
    if (!focusedLines) {
      return [];
    }
    return focusedLines.split(',').reduce(function (acc, index) {
      if (index.includes('-')) {
        var _index$split$map = index.split('-').map(Number),
          lowEdge = _index$split$map[0],
          highEdge = _index$split$map[1];
        var rangeArray = Array(highEdge - lowEdge + 1).fill(null).map(function (_, index) {
          return lowEdge + index;
        });
        return acc.concat(rangeArray);
      }
      return acc.concat(index);
    }, []).map(Number);
  }, [focusedLines]);
  var normalizedCode = useMemo(function () {
    return code.trim();
  }, [code]);
  var joinClassNames = useCallback(function (className) {
    return [className, classNameOverride].filter(Boolean).join(' ');
  }, [classNameOverride]);
  var getLineNumberClass = useCallback(function (lineIndex) {
    return ['line-number', focusedLineNumbers.includes(lineIndex + 1) && 'line-number-focused'].filter(Boolean).join(' ');
  }, [focusedLineNumbers]);
  var Preview = function Preview() {
    return createElement(Highlight, Object.assign({}, defaultProps, {
      code: normalizedCode,
      language: language,
      theme: theme
    }), function (_ref4) {
      var className = _ref4.className,
        style = _ref4.style,
        tokens = _ref4.tokens,
        getLineProps = _ref4.getLineProps,
        getTokenProps = _ref4.getTokenProps;
      return createElement(Pre, Object.assign({
        className: joinClassNames(className),
        style: style
      }, restProps), tokens.map(function (line, lineIndex) {
        return createElement(Line, Object.assign({}, getLineProps({
          line: line,
          key: lineIndex
        }), {
          className: getLineNumberClass(lineIndex),
          showLineNumbers: showLineNumbers
        }), showLineNumbers && createElement(LineNumber, null, lineIndex + lineNumberStart), line.map(function (token, key) {
          return createElement("span", Object.assign({}, getTokenProps({
            token: token,
            key: key
          })));
        }));
      }));
    });
  };
  return children({
    Preview: Preview,
    copyToClipboard: function copyToClipboard() {
      return navigator.clipboard.writeText(normalizedCode);
    }
  });
};
var CodeWithVariants = function CodeWithVariants(_ref) {
  var variants = _ref.variants,
    children = _ref.children;
  var _React$useState = useState(0),
    activeVariantIndex = _React$useState[0],
    setActiveVariantIndex = _React$useState[1];
  var activeVariant = useMemo(function () {
    return variants[activeVariantIndex];
  }, [variants, activeVariantIndex]);
  var setVariantIndex = function setVariantIndex(nextIndex) {
    setActiveVariantIndex(nextIndex);
  };
  return children({
    variants: variants,
    activeVariant: activeVariant,
    activeIndex: activeVariantIndex,
    setVariantIndex: setVariantIndex
  });
};
export { Code, CodeWithVariants };